import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@shared/components/ui/sidebar';
import { ChevronRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuItem as MenuItemType } from './types';

interface MenuItemProps {
  item: MenuItemType;
  className?: string;
  isNestedChild?: boolean; // Indica si este item es hijo de otro item con hijos
}

function toTestId(label: string | React.ReactNode) {
  if (typeof label === 'string') {
    return label
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '');
  }
  return 'custom-label';
}

// Función helper para determinar si una ruta está activa
function isRouteActive(itemPath: string, currentPath: string): boolean {
  if (!itemPath || itemPath === '#') return false;

  // Si la ruta es exactamente igual, está activa
  if (currentPath === itemPath) return true;

  // Si la ruta actual empieza con el path del item
  if (currentPath.startsWith(itemPath)) {
    // Verificar que el siguiente carácter sea '/' o el final de la cadena
    const nextChar = currentPath[itemPath.length];
    return nextChar === '/' || nextChar === undefined;
  }

  return false;
}

export default function MenuItem({ item, className, isNestedChild = false }: MenuItemProps) {
  const location = useLocation();
  const { isMobile, setOpenMobile, state: sidebarState } = useSidebar();
  const [isExpanded, setIsExpanded] = useState(() => Boolean(item.isMenuSection));
  const isSidebarCollapsed = sidebarState === 'collapsed';

  const handleLinkClick = () => {
    if (isMobile) {
      setTimeout(() => setOpenMobile(false), 150);
    }
  };

  const handleClick = () => {
    if (item.onClick) {
      item.onClick();
    }
    if (item.children) {
      setIsExpanded(!isExpanded);
    }
    handleLinkClick();
  };

  // Calcular si el item padre está activo
  const isActive = React.useMemo(() => {
    // Enlaces externos nunca están activos
    if (item.external) {
      return false;
    }

    const hasChildren = item.children && item.children.length > 0;

    // Si tiene hijos, NUNCA marcarlo como activo visualmente
    // (solo se expandirá automáticamente si algún hijo está activo)
    // Esto evita que el padre se marque cuando estás en una ruta hija
    if (hasChildren) {
      return false;
    }

    // Si NO tiene hijos y tiene path propio, usar la lógica normal de isRouteActive
    if (item.path && item.path !== '#') {
      return isRouteActive(item.path, location.pathname);
    }

    return false;
  }, [item, location.pathname]);

  // Calcular qué hijos están activos (recursivamente, incluyendo nietos)
  // Solo marcamos el hijo con la ruta más específica (más larga) que coincida
  const activeChildren = React.useMemo(() => {
    if (!item.children) return new Set<string>();

    // Función recursiva para encontrar todas las rutas activas en hijos y nietos
    const findActivePaths = (children: MenuItemType[]): Array<{ id: string; path: string; pathLength: number }> => {
      const matches: Array<{ id: string; path: string; pathLength: number }> = [];

      children.forEach((child) => {
        // Si el hijo tiene una ruta y está activo, agregarlo
        if (!child.external && child.path && child.path !== '#' && isRouteActive(child.path, location.pathname)) {
          matches.push({
            id: child.id,
            path: child.path,
            pathLength: child.path.length,
          });
        }

        // Si el hijo tiene hijos, buscar recursivamente en ellos
        if (child.children && child.children.length > 0) {
          const childMatches = findActivePaths(child.children);
          matches.push(...childMatches);
        }
      });

      return matches;
    };

    const matchingChildren = findActivePaths(item.children);

    // Si hay múltiples coincidencias, solo tomar la más específica (la más larga)
    if (matchingChildren.length === 0) return new Set<string>();

    // Ordenar por longitud de path (más largo primero)
    matchingChildren.sort((a, b) => b.pathLength - a.pathLength);

    // Solo marcar como activo el más específico
    const mostSpecific = matchingChildren[0];
    return new Set([mostSpecific.id]);
  }, [item.children, location.pathname]);

  // Expandir automáticamente si algún hijo está activo
  useEffect(() => {
    if (item.children && activeChildren.size > 0) {
      setIsExpanded(true);
    }
  }, [item.children, activeChildren.size]);

  const hasChildren = item.children && item.children.length > 0;

  if (item.isMenuSection && hasChildren) {
    const showSectionChildren = isSidebarCollapsed || isExpanded;

    return (
      <>
        <SidebarMenuItem
          className="pt-3 pb-0.5 group-data-[collapsible=icon]:hidden"
          data-testid={`menu-section-${toTestId(item.label)}`}
        >
          <SidebarMenuButton
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="h-auto py-1.5 text-[11px] font-medium uppercase tracking-wider text-sidebar-foreground/60 hover:text-sidebar-foreground/80 data-[active=true]:bg-transparent data-[active=true]:font-medium data-[active=true]:text-sidebar-foreground/60"
            data-testid={`menu-section-${toTestId(item.label)}-toggle`}
            aria-expanded={showSectionChildren}
          >
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronRight
              className={`h-3.5 w-3.5 shrink-0 transition-transform ${showSectionChildren ? 'rotate-90' : ''}`}
            />
          </SidebarMenuButton>
        </SidebarMenuItem>
        {showSectionChildren ? item.children?.map((child) => <MenuItem key={child.id} item={child} />) : null}
      </>
    );
  }

  if (item.isConfigBackButton) {
    return (
      <SidebarMenuItem key={item.id} className={className} data-testid="config-back-button">
        <SidebarMenuButton onClick={handleClick} className="text-sidebar-foreground">
          {item.icon}
          <span>{item.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  // Si es un hijo anidado, renderizar directamente como elemento principal del menú (sin sangría)
  if (isNestedChild) {
    return (
      <SidebarMenuItem key={item.id} className={`${className} ${item.path === '#' ? 'opacity-70' : ''} !ml-0`}>
        {hasChildren ? (
          <>
            <SidebarMenuButton
              onClick={() => setIsExpanded(!isExpanded)}
              isActive={false}
              className="data-[active=true]:!bg-transparent data-[active=true]:!text-sidebar-foreground data-[active=true]:!font-normal"
            >
              {item.icon}
              <span className="flex-1 group-data-[collapsible=icon]:hidden">{item.label}</span>
              <ChevronRight
                className={`transition-transform group-data-[collapsible=icon]:hidden ${isExpanded ? 'rotate-90' : ''}`}
              />
            </SidebarMenuButton>
            {isExpanded && (
              <SidebarMenuSub className="group-data-[collapsible=icon]:hidden">
                {item.children?.map((child) => {
                  const isChildActive = activeChildren.has(child.id);
                  return (
                    <SidebarMenuSubItem key={child.id}>
                      <SidebarMenuSubButton asChild isActive={isChildActive}>
                        {child.path && child.path !== '#' ? (
                          child.external ? (
                            <a
                              href={child.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={handleLinkClick}
                              data-testid={`submenu-${toTestId(child.label)}`}
                            >
                              {child.label}
                            </a>
                          ) : (
                            <Link
                              to={child.path}
                              onClick={handleLinkClick}
                              className={child.path === '#' ? 'opacity-70' : ''}
                              data-testid={`submenu-${toTestId(child.label)}`}
                              data-active={isChildActive}
                            >
                              {child.label}
                            </Link>
                          )
                        ) : (
                          <button
                            onClick={child.onClick}
                            className={`w-full text-left ${child.path === '#' ? 'opacity-70' : ''}`}
                            data-testid={`submenu-${toTestId(child.label)}`}
                          >
                            {child.label}
                          </button>
                        )}
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  );
                })}
              </SidebarMenuSub>
            )}
          </>
        ) : (
          <SidebarMenuButton asChild isActive={isActive}>
            {item.path && item.path !== '#' ? (
              item.external ? (
                <a
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleLinkClick}
                  data-testid={`menu-${toTestId(item.label)}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              ) : (
                <Link
                  to={item.path}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    } else {
                      handleLinkClick();
                    }
                  }}
                  className={item.path === '#' ? 'opacity-70' : ''}
                  data-testid={`menu-${toTestId(item.label)}`}
                  data-active={isActive}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              )
            ) : (
              <button
                onClick={handleClick}
                className={`w-full text-left ${item.path === '#' ? 'opacity-70' : ''}`}
                data-testid={`menu-${toTestId(item.label)}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            )}
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
    );
  }

  // Obtener los hijos que deben renderizarse fuera del <li> (elementos principales del menú)
  const nestedChildren =
    hasChildren && isExpanded
      ? item.children?.filter((child) => {
          const childHasChildren = child.children && child.children.length > 0;
          return childHasChildren;
        })
      : [];

  return (
    <>
      <SidebarMenuItem key={item.id} className={`${className} ${item.path === '#' ? 'opacity-70' : ''}`}>
        {hasChildren ? (
          <>
            <SidebarMenuButton
              onClick={() => setIsExpanded(!isExpanded)}
              isActive={false}
              className="data-[active=true]:!bg-transparent data-[active=true]: data-[active=true]:!font-normal"
            >
              {item.icon}
              <span className="flex-1 group-data-[collapsible=icon]:hidden">{item.label}</span>
              <ChevronRight
                className={`transition-transform group-data-[collapsible=icon]:hidden ${isExpanded ? 'rotate-90' : ''}`}
              />
            </SidebarMenuButton>
            {isExpanded && (
              <>
                {item.children?.map((child) => {
                  const childHasChildren = child.children && child.children.length > 0;

                  // Si el hijo tiene hijos, renderizarlo fuera del contexto del <li> padre
                  if (childHasChildren) {
                    return null;
                  }

                  // Si el hijo no tiene hijos, renderizarlo como elemento simple dentro del submenú
                  const isChildActive = activeChildren.has(child.id);
                  return (
                    <SidebarMenuSub key={`${item.id}-sub-${child.id}`} className="group-data-[collapsible=icon]:hidden">
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={isChildActive}>
                          {child.path && child.path !== '#' ? (
                            child.external ? (
                              <a
                                href={child.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleLinkClick}
                                data-testid={`submenu-${toTestId(child.label)}`}
                              >
                                {child.label}
                              </a>
                            ) : (
                              <Link
                                to={child.path}
                                onClick={handleLinkClick}
                                className={child.path === '#' ? 'opacity-70' : ''}
                                data-testid={`submenu-${toTestId(child.label)}`}
                                data-active={isChildActive}
                              >
                                {child.label}
                              </Link>
                            )
                          ) : (
                            <button
                              onClick={child.onClick}
                              className={`w-full text-left ${child.path === '#' ? 'opacity-70' : ''}`}
                              data-testid={`submenu-${toTestId(child.label)}`}
                            >
                              {child.label}
                            </button>
                          )}
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  );
                })}
              </>
            )}
          </>
        ) : (
          <SidebarMenuButton asChild isActive={isActive}>
            {item.path && item.path !== '#' ? (
              item.external ? (
                <a
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleLinkClick}
                  data-testid={`menu-${toTestId(item.label)}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              ) : (
                <Link
                  to={item.path}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    } else {
                      handleLinkClick();
                    }
                  }}
                  className={item.path === '#' ? 'opacity-70' : ''}
                  data-testid={`menu-${toTestId(item.label)}`}
                  data-active={isActive}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              )
            ) : (
              <button
                onClick={handleClick}
                className={`w-full text-left ${item.path === '#' ? 'opacity-70' : ''}`}
                data-testid={`menu-${toTestId(item.label)}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            )}
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
      {/* Renderizar los hijos que deben ser elementos principales del menú fuera del <li> padre */}
      {isExpanded && nestedChildren && nestedChildren.length > 0 && (
        <>
          {nestedChildren.map((child) => (
            <MenuItem key={`${item.id}-${child.id}`} item={child} isNestedChild={true} />
          ))}
        </>
      )}
    </>
  );
}
